import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import styled, { css } from 'styled-components';

// Components:
import { Label as UILabel } from '@lt/components';
import { ArrowDown } from '@lt/components/icons-react';

// Types
import { useTranslation } from '@lt/localization';
import type { TLabelStyle } from '@lt/components/components/Label/Label.model';
import type { ILabelList } from './types';
import { LABEL_LIST_TEST_ID } from './constants';

export const LabelList = (props: ILabelList) => {
  const { labels, badges = [], className, isMobile = false } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [overflown, setOverflown] = useState(false);
  useEffect(() => {
    const { scrollWidth, clientWidth } = containerRef.current || {};
    const isOverflown = isMobile
      ? labels?.length + badges?.length > 3
      : (scrollWidth || 0) > (clientWidth || 0);
    setOverflown(isOverflown);
  }, [labels, badges, isMobile]);

  const [expanded, setExpanded] = useState(false);
  const handleClick = () => setExpanded(true);
  const { t } = useTranslation();

  if (isEmpty(labels) && isEmpty(badges)) return null;

  const handleContainerClick = () => {
    if (!isMobile) return;
    setExpanded(true);
  };
  const showExpandButton = !isMobile && overflown && !expanded;

  return (
    <StyledWrapper
      ref={containerRef}
      className={className}
      isMobile={isMobile}
      expanded={expanded}
      overflown={overflown}
      showExpandButton={showExpandButton}
      onClick={handleContainerClick}
      data-testid={LABEL_LIST_TEST_ID}
    >
      {labels?.map((label) => {
        const { id, title, description, icon_path: iconPath } = label;
        return (
          <StyledLabel
            key={id}
            title={title}
            numberOfStyle={'LABEL' as TLabelStyle}
            description={description}
            iconPath={iconPath}
            isMobile={isMobile}
          />
        );
      })}
      {badges?.map((badge, index) => {
        const { text, badge_data: badgeData } = badge;
        const badgeText = index === 0 ? text : badgeData.category_name;

        return (
          <StyledLabel
            key={badgeText}
            title={badgeText}
            numberOfStyle={'LABEL' as TLabelStyle}
            isMobile={isMobile}
          />
        );
      })}
      {showExpandButton ? (
        <StyledButton type="button" onClick={handleClick}>
          {t('hotel.offer.still')}
          <StyledIcon />
        </StyledButton>
      ) : null}
    </StyledWrapper>
  );
};

const desktopStyles = css<{ expanded: boolean; showExpandButton: boolean }>`
  flex-direction: row;
  flex-wrap: nowrap;
  margin-top: 3px;
  max-width: 100%;

  ${(props) =>
    props.showExpandButton &&
    `&:after {
      position: absolute;
      content: '';
      width: 64px;
      height: 24px;
      top: 0;
      right: 64px;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff);
    }`}

  ${({ expanded }) => expanded && 'flex-wrap: wrap;'}
`;

const mobileStyles = css<{ expanded: boolean; overflown: boolean }>`
  flex-wrap: wrap;
  margin-top: 4px;
  max-width: 100%;
  max-height: ${({ expanded }) => (expanded ? 'none' : '50px')};

  ${({ overflown, expanded }) =>
    overflown &&
    !expanded &&
    `
      &:after {
        right: 0;
        bottom: -10px;
        left: 0;
        position absolute;
        width: 100%;
        height: 40px;
        content: '';
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.0001) 0%, rgba(255, 255, 255, 0.7) 32.29%, #FFFFFF 81.25%);
      }
    `}
`;

const StyledWrapper = styled.div<{
  isMobile: boolean;
  overflown: boolean;
  expanded: boolean;
  showExpandButton: boolean;
}>`
  display: flex;
  overflow: hidden;
  position: relative;

  ${({ isMobile }) => (isMobile ? mobileStyles : desktopStyles)}
`;

const StyledLabel = styled(({ numberOfStyle, ...props }) => (
  <UILabel style={numberOfStyle} {...props} />
))<{
  isMobile: boolean;
}>`
  max-width: 100%;
  margin-bottom: 8px;

  ${({ isMobile }) => isMobile && 'margin: 0; margin-bottom: 4px !important;'}
`;

const StyledButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;

  width: 64px;
  height: 24px;
  text-align: center;
  color: #007aff;
  background-color: #fff;

  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
`;

const StyledIcon = styled(ArrowDown)`
  transform: rotate(180deg);
  margin-left: 4px;
  stroke: #007aff;
  width: 10px;
  height: 20px;
`;
